

// src/app/modules/Settings/BillingTab.jsx
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import {
  CreditCard, CheckCircle, AlertCircle, Clock, XCircle,
  RefreshCw, Trash2, Star, AlertTriangle, Zap,
  Check, X, LayoutGrid, FileText, ChevronRight, ArrowUpRight,
  Receipt, Calendar, Hash, ExternalLink, Copy, Tag, Gift,
  TrendingUp, TrendingDown,
} from "lucide-react";
import { toast } from "react-toastify";
import * as dashActions from "../../Dashbord/_redux/dashboard/Actions";
import * as billingCrud from "../../Dashbord/_redux/dashboard/Crud.billing";
import './billing.scss';

// ── Helpers ───────────────────────────────────────────────────────────────────

const CURRENCY_CONFIG = {
  NGN: { symbol: '₦', locale: 'en-NG', decimals: 0 },
  USD: { symbol: '$', locale: 'en-US', decimals: 2 },
};

const formatAmount = (amount, currency = 'NGN') => {
  const cfg = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.NGN;
  const num = (amount || 0) / 100;
  return `${cfg.symbol}${num.toLocaleString(cfg.locale, { minimumFractionDigits: cfg.decimals })}`;
};

// Derive org currency from country_code — NG or unset (legacy) → NGN, everything else → USD
const getOrgCurrency = (org) => (!org?.country_code || org.country_code === 'NG' ? 'NGN' : 'USD');

// Pick the plan pricing object for the given currency
const getPlanPricing = (plan, currency) => {
  if (currency === 'USD') {
    const variant = (plan.pricing_variants || []).find(v => v.currency === 'USD');
    if (variant) return variant;
  }
  return plan.pricing || {};
};

const formatDate = (date) =>
  new Date(date).toLocaleDateString("en-US", {
    year: "numeric", month: "short", day: "numeric",
  });

const formatDateTime = (date) =>
  new Date(date).toLocaleString("en-US", {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

const daysBetween = (a, b) =>
  Math.ceil((new Date(b) - new Date(a)) / (1000 * 60 * 60 * 24));

const STATUS_CONFIG = {
  active:    { label: "Active",    cls: "status-active",    icon: CheckCircle },
  trial:     { label: "Trial",     cls: "status-trial",     icon: Zap },
  past_due:  { label: "Past Due",  cls: "status-past-due",  icon: AlertCircle },
  suspended: { label: "Suspended", cls: "status-suspended", icon: XCircle },
  cancelled: { label: "Cancelled", cls: "status-cancelled", icon: XCircle },
};

const INVOICE_STATUS = {
  paid:  { label: "Paid",  cls: "inv-paid",  dot: "#16a34a" },
  open:  { label: "Due",   cls: "inv-open",  dot: "#dc2626" },
  draft: { label: "Draft", cls: "inv-draft", dot: "#94a3b8" },
  void:  { label: "Void",  cls: "inv-void",  dot: "#94a3b8" },
};

// ── PlanCard ──────────────────────────────────────────────────────────────────

// Extract the feature keys that are true for a plan
const getPlanFeatures = (plan) =>
  Object.entries(plan.features || {})
    .filter(([, v]) => v === true)
    .map(([k]) => k);

const formatFeatureLabel = (k) =>
  k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

/**
 * Build the feature list for a plan card using inheritance.
 *
 * Plans sorted cheapest → most expensive. Each plan shows:
 *   own        = features unique to this plan (not on any cheaper plan)
 *   inherited  = features carried over from cheaper plans
 *
 * Starter: shows only own features (nothing inherited)
 * Growth:  shows own new features + all Starter features
 * Scale:   shows own new features + all Starter + Growth features
 */
// function buildInheritedFeatures(plan, allPlans) {
//   const planPrice = (p) =>
//     p.pricing?.per_unit_pricing?.amount_per_unit ||
//     p.pricing?.perUnitPricing?.amountPerUnit ||
//     p.pricing?.base_amount ||
//     p.pricing?.baseAmount || 0;

//   const sorted = [...allPlans].sort((a, b) => planPrice(a) - planPrice(b));
//   const thisId = plan.plan_id || plan._id;
//   const thisPlanFeatures = new Set(getPlanFeatures(plan));
//   const inheritedSet = new Set();

//   for (const p of sorted) {
//     if ((p.plan_id || p._id) === thisId) break;
//     getPlanFeatures(p).forEach(f => inheritedSet.add(f));
//   }

//   const own       = getPlanFeatures(plan).filter(f => !inheritedSet.has(f));
//   const inherited = [...inheritedSet].filter(f => thisPlanFeatures.has(f));

//   return { own, inherited };
// }


function buildInheritedFeatures(plan, allPlans) {
  // 1. Helper to calculate a numeric "weight" for sorting
  const getPlanPrice = (p) => {
    if (p.is_custom) return Infinity; // Enterprise is always last
    const pu = p.pricing?.per_unit_pricing || {};
    return (p.pricing?.base_amount || 0) + (pu.amount_per_unit || 0) + (pu.min_charge || 0);
  };

  // 2. Sort all plans by price
  const sorted = [...allPlans].sort((a, b) => getPlanPrice(a) - getPlanPrice(b));
  
  // 3. Find the index of the current plan
  const thisIndex = sorted.findIndex(p => (p.plan_id || p._id) === (plan.plan_id || plan._id));
  
  // 4. Identify the immediate predecessor
  const previousPlan = thisIndex > 0 ? sorted[thisIndex - 1] : null;

  // 5. Get boolean feature keys for both
  const getTrueFeatures = (p) => 
    Object.keys(p.features || {}).filter(key => p.features[key] === true);

  const currentTrueFeatures = getTrueFeatures(plan);
  const prevTrueFeatures = previousPlan ? getTrueFeatures(previousPlan) : [];

  // 6. Logic: "Own" are features present here but NOT in the plan below
  const own = currentTrueFeatures.filter(f => !prevTrueFeatures.includes(f));
  
  // 7. Logic: "Inherited" is now just a reference to the previous plan's name for the UI
  // We return the name so the UI can render: "Everything in [Business Starter]"
  return { 
    own, 
    previousPlanName: previousPlan ? previousPlan.name : null 
  };
}

function PlanCard({ plan, allPlans, currentPlanId, onSelect, loading, anyLoading, hasActiveSub, currency = 'NGN' }) {
  const isCurrentPlan = plan.plan_id === currentPlanId || plan.slug === currentPlanId;
  const fmt = (amount) => formatAmount(amount, currency);

  // Pick pricing object for the org's currency
  const pricing    = getPlanPricing(plan, currency);
  const pu         = pricing.per_unit_pricing || pricing.perUnitPricing || {};
  const isPerUnit  = plan.pricing_model === "per_unit" || plan.pricingModel === "per_unit" || pu?.enabled === true;
  const isEstate   = pu?.unit_type === "resident" || pu?.unitType === "resident";
  const unitPrice  = pu?.amount_per_unit || pu?.amountPerUnit || 0;
  const minCharge  = pu?.min_charge || pu?.minCharge || 0;
  const baseAmount = pricing.base_amount || pricing.baseAmount || 0;

  const priceDisplay = plan.is_custom
    ? <span className="plan-card__custom">Contact Sales</span>
    : isEstate && unitPrice > 0
      ? <>{fmt(unitPrice)}<small>/resident/mo</small></>
      : isPerUnit && unitPrice > 0
        ? <>{fmt(unitPrice)}<small>/staff/mo</small></>
        : baseAmount > 0
          ? <>{fmt(baseAmount)}<small>/mo</small></>
          : <span className="plan-card__custom">Free</span>;

  const minChargeNote = !plan.is_custom && (isPerUnit || isEstate) && minCharge > 0
    ? `Min ${fmt(minCharge)}/mo`
    : null;

  // const { own, inherited } = buildInheritedFeatures(plan, allPlans);
  const { own, previousPlanName } = buildInheritedFeatures(plan, allPlans);

  const SALES_EMAIL = "info@sety.io";
  const handleContactSales = () => {
    const subject = encodeURIComponent("Enterprise Plan Enquiry");
    const body = encodeURIComponent(
      `Hi Sety team,\n\nI'm interested in the Enterprise plan for my organisation.\n\nOrganisation name: \nEstimated size: \nUse case: \n\nPlease get in touch.\n\nThanks`
    );
    window.location.href = `mailto:${SALES_EMAIL}?subject=${subject}&body=${body}`;
  };

  const btnLabel = () => {
    if (isCurrentPlan)  return "Current Plan";
    if (plan.is_custom) return "Contact Sales";
    if (hasActiveSub)   return "Switch to Plan";
    return "Subscribe";   // ← was "Select Plan"
  };

  
  return (
    <div className={`plan-card ${isCurrentPlan ? "plan-card--current" : ""}`}>
      {isCurrentPlan && <div className="plan-card__badge">Current Plan</div>}

      {/* Clean loader overlay while this plan is being processed */}
      {loading && (
        <div className="plan-card__loader">
          <RefreshCw size={22} className="spin" />
          <span>{hasActiveSub ? "Switching plan…" : "Redirecting to payment…"}</span>
        </div>
      )}

      <div className="plan-card__name">{plan.name}</div>
      <div className="plan-card__price">{priceDisplay}</div>
      {minChargeNote && <div className="plan-card__min-charge">{minChargeNote}</div>}

      <p className="plan-card__desc">{plan.description}</p>

      <ul className="plan-card__features">
        {/* Own features — what's new on this plan */}
        {own.map(k => (
          <li key={k} className="plan-card__feature--own">
            <Check size={11} />{formatFeatureLabel(k)}
          </li>
        ))}

        {/* Divider + inherited features from cheaper plans */}
        {/* {inherited.length > 0 && (
          <>
            <li className="plan-card__feature-divider">
              Everything in lower plans
            </li>
            {inherited.map(k => (
              <li key={k} className="plan-card__feature--inherited">
                <Check size={11} />{formatFeatureLabel(k)}
              </li>
            ))}
          </>
        )} */}

        {previousPlanName && (
          <li className="plan-card__feature--own">
            <Check size={11} /> Everything in {previousPlanName}
          </li>
        )}
      </ul>

      <button
        className={`btn btn-sm w-100 ${isCurrentPlan ? "btn-light" : "btn-primary"}`}
        disabled={isCurrentPlan || (anyLoading && !plan.is_custom)}
        onClick={() => {
          if (plan.is_custom) return handleContactSales();
          if (!isCurrentPlan) onSelect(plan.plan_id || plan._id);
        }}
      >
        {btnLabel()}
      </button>
    </div>
  );
}

// ── Plans Modal ───────────────────────────────────────────────────────────────

function PlansModal({ plans, currentPlanId, onSelect, subscribingPlan, onClose, hasActiveSub, currency }) {
  return (
    <div className="billing-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="plans-modal">
        <div className="plans-modal__head">
          <div>
            <h6 className="plans-modal__title">
              {hasActiveSub ? "Change Plan" : "Choose a Plan"}
            </h6>
            <p className="plans-modal__sub">
              {hasActiveSub
                ? "Upgrades are charged immediately (prorated). Downgrades credit your next invoice."
                : "Select the plan that works best for your organisation."}
            </p>
          </div>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="plans-modal__body">
          <div className="plans-grid">
            {plans.map(p => (
              <PlanCard
                key={p.plan_id || p._id}
                plan={p}
                allPlans={plans}
                currentPlanId={currentPlanId}
                onSelect={onSelect}
                loading={subscribingPlan === (p.plan_id || p._id)}
                anyLoading={!!subscribingPlan}
                hasActiveSub={hasActiveSub}
                currency={currency}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Promo Code Modal ──────────────────────────────────────────────────────────

function PromoModal({ onApply, applying, onClose }) {
  const [code, setCode] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.trim()) onApply(code.trim());
  };

  return (
    <div className="billing-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="billing-dialog">
        <div className="billing-dialog__icon-row">
          <div className="billing-dialog__icon billing-dialog__icon--brand">
            <Tag size={18} />
          </div>
          <button className="icon-btn" onClick={onClose}><X size={15} /></button>
        </div>
        <h6>Apply Promo Code</h6>
        <p>Enter your promo code below. The discount will be applied to your next invoice automatically.</p>
        <form onSubmit={handleSubmit}>
          <div className="promo-input-row">
            <input
              className="form-control form-control-sm promo-input"
              placeholder="e.g. LAUNCH50"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              autoFocus
              maxLength={50}
            />
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={applying || !code.trim()}
            >
              {applying
                ? <RefreshCw size={13} className="spin" />
                : "Apply"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Invoice Detail Modal ──────────────────────────────────────────────────────

function InvoiceModal({ invoice, onClose, onPay, paying, orgCurrency = 'NGN' }) {
  const sc = INVOICE_STATUS[invoice.status] || INVOICE_STATUS.draft;
  const isOpen = invoice.status === "open";
  const currency = invoice.currency || orgCurrency;
  const fmt = (amount) => formatAmount(amount, currency);
  const periodDays = invoice.period_start && invoice.period_end
    ? daysBetween(invoice.period_start, invoice.period_end)
    : null;

  const handleCopyId = () => {
    navigator.clipboard.writeText(invoice.invoice_id);
    toast.success("Invoice ID copied");
  };

  return (
    <div className="billing-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="invoice-modal">

        {/* Header */}
        <div className="invoice-modal__header">
          <div className="invoice-modal__header-left">
            <div className="invoice-modal__icon"><Receipt size={20} /></div>
            <div>
              <div className="invoice-modal__number">{invoice.invoice_number}</div>
              <div className="invoice-modal__id">
                {invoice.invoice_id}
                <button className="copy-btn" onClick={handleCopyId} title="Copy invoice ID">
                  <Copy size={11} />
                </button>
              </div>
            </div>
          </div>
          <div className="invoice-modal__header-right">
            <span className={`inv-badge ${sc.cls}`}>{sc.label}</span>
            <button className="icon-btn" onClick={onClose}><X size={16} /></button>
          </div>
        </div>

        <div className="invoice-modal__body">

          {/* Meta row */}
          <div className="invoice-meta-grid">
            <div className="invoice-meta-item">
              <Calendar size={13} className="invoice-meta-item__icon" />
              <div>
                <div className="invoice-meta-item__label">Billing Period</div>
                <div className="invoice-meta-item__value">
                  {formatDate(invoice.period_start)} — {formatDate(invoice.period_end)}
                  {periodDays && <span className="period-days">{periodDays}d</span>}
                </div>
              </div>
            </div>
            <div className="invoice-meta-item">
              <Clock size={13} className="invoice-meta-item__icon" />
              <div>
                <div className="invoice-meta-item__label">Due Date</div>
                <div className={`invoice-meta-item__value ${isOpen ? "text-danger" : ""}`}>
                  {formatDate(invoice.due_date)}
                </div>
              </div>
            </div>
            <div className="invoice-meta-item">
              <Hash size={13} className="invoice-meta-item__icon" />
              <div>
                <div className="invoice-meta-item__label">Created</div>
                <div className="invoice-meta-item__value">{formatDate(invoice.created_at)}</div>
              </div>
            </div>
            {invoice.paid_at && (
              <div className="invoice-meta-item">
                <CheckCircle size={13} className="invoice-meta-item__icon success" />
                <div>
                  <div className="invoice-meta-item__label">Paid On</div>
                  <div className="invoice-meta-item__value">{formatDateTime(invoice.paid_at)}</div>
                </div>
              </div>
            )}
          </div>

          {/* Line items */}
          <div className="invoice-line-items">
            <div className="invoice-line-items__header">
              <span>Description</span>
              <span>Qty</span>
              <span>Unit Price</span>
              <span>Amount</span>
            </div>
            {(invoice.line_items || []).map((item, i) => (
              <div key={item._id || i} className="invoice-line-item">
                <div className="invoice-line-item__desc">
                  <span className="invoice-line-item__type">
                    {item.type === "per_unit" ? "Usage" : item.type === "overage" ? "Overage" : "Fixed"}
                  </span>
                  {item.description}
                </div>
                <div className="invoice-line-item__qty">{item.quantity}</div>
                <div className="invoice-line-item__unit">{fmt(Math.abs(item.unit_amount))}</div>
                <div className="invoice-line-item__amount">
                  {item.amount < 0 ? "−" : ""}{fmt(Math.abs(item.amount))}
                </div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="invoice-totals">
            <div className="invoice-totals__row">
              <span>Subtotal</span>
              <span>{fmt(invoice.subtotal)}</span>
            </div>
            {invoice.credit_applied > 0 && (
              <div className="invoice-totals__row invoice-totals__row--discount">
                <span>Credit Applied</span>
                <span>−{fmt(invoice.credit_applied)}</span>
              </div>
            )}
            {invoice.tax_amount > 0 && (
              <div className="invoice-totals__row">
                <span>Tax</span>
                <span>{fmt(invoice.tax_amount)}</span>
              </div>
            )}
            <div className="invoice-totals__row invoice-totals__row--total">
              <span>Total</span>
              <span>{fmt(invoice.total)}</span>
            </div>
            {invoice.amount_paid > 0 && invoice.status === "paid" && (
              <div className="invoice-totals__row invoice-totals__row--paid">
                <span>Amount Paid</span>
                <span>{fmt(invoice.amount_paid)}</span>
              </div>
            )}
            {invoice.amount_due > 0 && invoice.status !== "paid" && (
              <div className="invoice-totals__row invoice-totals__row--due">
                <span>Amount Due</span>
                <span>{fmt(invoice.amount_due)}</span>
              </div>
            )}
          </div>

          {/* Charge attempts */}
          {invoice.charge_attempts?.length > 0 && (
            <div className="charge-attempts">
              <div className="charge-attempts__title">Payment Attempts</div>
              {invoice.charge_attempts.map((attempt, i) => (
                <div key={i} className="charge-attempt">
                  <span className={`charge-attempt__status ${attempt.status === "success" ? "success" : attempt.status === "pending" ? "pending" : "failed"}`}>
                    {attempt.status === "success"
                      ? <CheckCircle size={12} />
                      : attempt.status === "pending"
                        ? <RefreshCw size={12} className="spin" />
                        : <XCircle size={12} />}
                    {attempt.status === "success" ? "Succeeded" : attempt.status === "pending" ? "Pending" : "Failed"}
                  </span>
                  <span className="charge-attempt__date">{formatDateTime(attempt.attempted_at)}</span>
                  {/* FIX: field is failure_reason not failure_message */}
                  {attempt.failure_reason && (
                    <span className="charge-attempt__msg">{attempt.failure_reason}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="invoice-modal__footer">
          <button className="sety-btn sety-btn-ghost" onClick={onClose}>Close</button>
          {isOpen && (
            <button
              className="btn btn-primary btn-sm"
              disabled={paying}
              onClick={() => onPay(invoice.invoice_id)}
            >
              {paying
                ? <><RefreshCw size={13} className="spin mr-1" /> Processing...</>
                : <><ArrowUpRight size={13} className="mr-1" /> Pay {fmt(invoice.amount_due)}</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Paystack Payment Modal ────────────────────────────────────────────────────

function PaystackModal({ paymentUrl, invoiceNumber, amount, currency = 'NGN', onClose }) {
  return (
    <div className="billing-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="paystack-modal">
        <div className="paystack-modal__header">
          <div className="paystack-modal__icon"><CreditCard size={22} /></div>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="paystack-modal__body">
          <h5 className="paystack-modal__title">Complete Payment</h5>
          <p className="paystack-modal__sub">
            Pay <strong>{formatAmount(amount, currency)}</strong> for invoice <strong>{invoiceNumber}</strong>
          </p>
          <div className="paystack-methods">
            <div className="paystack-methods__label">Accepted payment methods</div>
            <div className="paystack-methods__icons">
              <span className="payment-method-pill">💳 Card</span>
              <span className="payment-method-pill">🏦 Bank Transfer</span>
              <span className="payment-method-pill">📱 USSD</span>
              <span className="payment-method-pill">🏧 Bank</span>
            </div>
          </div>
          <div className="paystack-notice">
            <AlertTriangle size={13} />
            You'll be redirected to Paystack's secure checkout. Your invoice updates automatically
            once payment is confirmed — you'll also receive a confirmation email.
          </div>
        </div>
        <div className="paystack-modal__footer">
          <button className="sety-btn sety-btn-ghost" onClick={onClose}>Cancel</button>
          <a
            href={paymentUrl}
            target="_blank"
            rel="noreferrer"
            className="btn btn-primary btn-sm"
            onClick={onClose}
          >
            <ExternalLink size={13} className="mr-1" />
            Open Paystack Checkout
          </a>
        </div>
      </div>
    </div>
  );
}

// ── Plan Change Confirmation Modal ────────────────────────────────────────────

function PlanChangeConfirmModal({ fromPlan, toPlan, onConfirm, onClose, loading, currency = 'NGN' }) {
  const fromPricing = getPlanPricing(fromPlan || {}, currency);
  const toPricing   = getPlanPricing(toPlan   || {}, currency);
  const isUpgrade = (toPricing?.base_amount || 0) >= (fromPricing?.base_amount || 0);
  const Icon = isUpgrade ? TrendingUp : TrendingDown;
  const iconCls = isUpgrade ? "billing-dialog__icon--success" : "billing-dialog__icon--warning";

  return (
    <div className="billing-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="billing-dialog">
        <div className="billing-dialog__icon-row">
          <div className={`billing-dialog__icon ${iconCls}`}>
            <Icon size={18} />
          </div>
          <button className="icon-btn" onClick={onClose}><X size={15} /></button>
        </div>
        <h6>{isUpgrade ? "Confirm Upgrade" : "Confirm Downgrade"}</h6>
        <p>
          You're switching from <strong>{fromPlan?.name}</strong> to <strong>{toPlan?.name}</strong>.
          {isUpgrade
            ? " The prorated difference will be charged to your saved card immediately."
            : " Your remaining credit will be applied to your next invoice — no refund is issued."}
        </p>
        <div className="billing-dialog__actions">
          <button className="sety-btn sety-btn-ghost" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className={`btn btn-sm ${isUpgrade ? "btn-primary" : "btn-warning"}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading
              ? <><RefreshCw size={13} className="spin mr-1" /> Processing...</>
              : `Confirm ${isUpgrade ? "Upgrade" : "Downgrade"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── BillingTab ────────────────────────────────────────────────────────────────

export function BillingTab({ initialAction }) {
  const dispatch = useDispatch();

  const [payingInvoice, setPayingInvoice]           = useState(null);
  const [removingPm, setRemovingPm]                 = useState(null);
  const [settingDefault, setSettingDefault]         = useState(null);
  const [cancelling, setCancelling]                 = useState(false);
  const [showCancelConfirm, setShowCancelConfirm]   = useState(false);
  const [subscribingPlan, setSubscribingPlan]       = useState(null);
  const [showPlansModal, setShowPlansModal]         = useState(initialAction === "subscribe");
  const [selectedInvoice, setSelectedInvoice]       = useState(null);
  const [paystackData, setPaystackData]             = useState(null);
  const [showPromoModal, setShowPromoModal]         = useState(false);
  const [applyingPromo, setApplyingPromo]           = useState(false);
  // Plan change confirmation: { planId, planObj }
  const [pendingPlanChange, setPendingPlanChange]   = useState(null);
  const [confirmingChange, setConfirmingChange]     = useState(false);

  const { org, subscription, billingPlans, invoices, invoicesMeta, paymentMethods } = useSelector(
    (state) => ({
      org:            state.auth.organization,
      subscription:   state.dashboard.subscription,
      billingPlans:   state.dashboard.billingPlans   || [],
      invoices:       state.dashboard.invoices       || [],
      invoicesMeta:   state.dashboard.invoicesMeta,
      paymentMethods: state.dashboard.paymentMethods || [],
    }),
    shallowEqual
  );

  const orgId      = org?.organization_id;
  const sub        = subscription?.subscription;
  const plan       = subscription?.plan;
  const orgCurrency = getOrgCurrency(org);
  const fmt        = (amount, currency = orgCurrency) => formatAmount(amount, currency);

  useEffect(() => {
    if (!orgId) return;
    dispatch(dashActions.fetchAllData("subscription", orgId));
    dispatch(dashActions.fetchAllData("billing_plans", org?.type));
    dispatch(dashActions.fetchAllData("invoices", orgId, { page: 1, limit: 10 }));
    dispatch(dashActions.fetchAllData("payment_methods", orgId));
  }, [orgId]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  /**
   * Handle plan selection from the PlansModal.
   * - No active subscription → initializeFirstPayment (Paystack redirect)
   * - Active subscription   → show confirmation modal before calling changePlan
   */
  function handlePlanSelect(plan_id) {
    const isActive = sub?.status === "active" || sub?.status === "past_due";
    if (isActive) {
      const toPlan = displayPlans.find(p => (p.plan_id || p._id) === plan_id);
      setPendingPlanChange({ plan_id, planObj: toPlan });
      setShowPlansModal(false);
    } else {
      handleSubscribe(plan_id);
    }
  }

  async function handleSubscribe(plan_id) {
    setSubscribingPlan(plan_id);
    try {
      const res = await billingCrud.initializeSubscription(orgId, {
        plan_id,
        callback_url: `${window.location.origin}/settings?tab=billing&status=success`,
      });
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to start payment");
      setSubscribingPlan(null);
    }
  }

  async function handleConfirmPlanChange() {
    if (!pendingPlanChange) return;
    setConfirmingChange(true);
    try {
      await billingCrud.changePlan(orgId, { plan_id: pendingPlanChange.plan_id });
      toast.success("Plan updated successfully.");
      setPendingPlanChange(null);
      dispatch(dashActions.fetchAllData("subscription", orgId));
      dispatch(dashActions.fetchAllData("invoices", orgId, { page: 1, limit: 10 }));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to change plan. Please try again.");
    } finally {
      setConfirmingChange(false);
    }
  }

  /**
   * Pay invoice — backend decides strategy (saved card or Paystack link).
   * FIX: fallback now calls createInvoicePaymentLink with correct param structure.
   */
  async function handlePayInvoice(invoice_id) {
    setPayingInvoice(invoice_id);
    const invoice = invoices.find((i) => i.invoice_id === invoice_id);

    try {
      const res = await billingCrud.payInvoice(orgId, invoice_id);
      const data = res.data;

      if (data.authorization_url) {
        setPaystackData({
          url: data.authorization_url,
          invoiceNumber: invoice?.invoice_number,
          amount: invoice?.amount_due || invoice?.total,
        });
        setSelectedInvoice(null);
      } else {
        toast.info("Payment initiated — your invoice will update once confirmed.", { autoClose: 6000 });
        setSelectedInvoice(null);
        setTimeout(() => {
          dispatch(dashActions.fetchAllData("invoices", orgId, { page: 1, limit: 10 }));
          dispatch(dashActions.fetchAllData("subscription", orgId));
        }, 3000);
      }
    } catch {
      // Fallback: explicitly request a payment link
      // FIX: pass invoice_id as URL param, body has callback_url only
      try {
        const res = await billingCrud.createInvoicePaymentLink(orgId, invoice_id, {
          callback_url: `${window.location.origin}/settings?tab=billing&status=success`,
        });
        setPaystackData({
          url: res.data.authorization_url,
          invoiceNumber: invoice?.invoice_number,
          amount: invoice?.amount_due || invoice?.total,
        });
        setSelectedInvoice(null);
      } catch (fallbackErr) {
        toast.error(fallbackErr?.response?.data?.message || "Payment could not be initiated. Please try again.");
      }
    } finally {
      setPayingInvoice(null);
    }
  }

  async function handleApplyPromo(code) {
    setApplyingPromo(true);
    try {
      const res = await billingCrud.applyPromoCode(orgId, { code });
      toast.success(res.data?.message || "Promo applied!");
      setShowPromoModal(false);
      dispatch(dashActions.fetchAllData("subscription", orgId));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Invalid or expired promo code.");
    } finally {
      setApplyingPromo(false);
    }
  }

  async function handleSetDefault(payment_method_id) {
    setSettingDefault(payment_method_id);
    try {
      await dispatch(dashActions.addData("set_default_payment_method", { organization_id: orgId, payment_method_id }, orgId));
    } finally {
      setSettingDefault(null);
    }
  }

  async function handleRemovePm(payment_method_id) {
    setRemovingPm(payment_method_id);
    try {
      await dispatch(dashActions.addData("remove_payment_method", { organization_id: orgId, payment_method_id }, orgId));
    } finally {
      setRemovingPm(null);
    }
  }

  async function handleCancelSubscription() {
    setCancelling(true);
    try {
      await dispatch(dashActions.addData("cancel_subscription", {}, orgId));
      setShowCancelConfirm(false);
      dispatch(dashActions.fetchAllData("subscription", orgId));
    } finally {
      setCancelling(false);
    }
  }

  // ── Derived ───────────────────────────────────────────────────────────────

  const statusCfg  = STATUS_CONFIG[sub?.status] || STATUS_CONFIG.trial;
  const StatusIcon = statusCfg.icon;
  const isTrial    = sub?.status === "trial";
  const isActive   = sub?.status === "active";
  const hasActiveSub = isActive || sub?.status === "past_due";

  const pendingCredit = sub?.pending_credit_amount || 0;

  const displayPlans = billingPlans
    .filter(p =>
      p.applicable_org_types?.includes("all") ||
      p.applicable_org_types?.includes(org?.type)
    )
    .sort((a, b) => {
      const aAll = a.applicable_org_types?.includes("all");
      const bAll = b.applicable_org_types?.includes("all");
      if (aAll && !bAll) return 1;
      if (!aAll && bAll) return -1;
      return (a.pricing?.per_unit_pricing?.amount_per_unit || 0) - (b.pricing?.per_unit_pricing?.amount_per_unit || 0);
    });

  // For plan change confirm modal — find the current plan object
  const currentPlanObj = displayPlans.find(p => p.plan_id === sub?.plan_id || p.slug === plan?.slug);

  const isLocked = sub?.status === 'suspended' || sub?.status === 'cancelled';


  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="billing-tab">

      {/* ── Subscription ─────────────────────────────────────────────── */}
      <div className="billing-section">
        <div className="billing-section__head">
          <h6 className="billing-section__title">Subscription</h6>
          {/* Promo code button — only show on active/trial */}
          {(isActive || isTrial) && (
            <button className="btn-text-link" onClick={() => setShowPromoModal(true)}>
              <Tag size={12} className="mr-1" /> Promo Code
            </button>
          )}
        </div>
        <div className="billing-section__body">
          <div className="sub-row">
            <div className="sub-row__info">
              <div className="sub-row__plan">{plan?.name || "Free Trial"}</div>
              <span className={`sub-status ${statusCfg.cls}`}>
                <StatusIcon size={11} />
                {statusCfg.label}
              </span>
            </div>
            <div className="sub-row__meta">
              {isTrial && sub?.trial_ends_at && (
                <div className="meta-pill meta-pill--warning">
                  <AlertTriangle size={12} />
                  Trial ends {formatDate(sub.trial_ends_at)}
                </div>
              )}
              {isActive && sub?.current_period_end && (
                <div className="meta-pill">
                  <Clock size={12} />
                  Renews {formatDate(sub.current_period_end)}
                </div>
              )}
              {sub?.status === "past_due" && (
                <div className="meta-pill meta-pill--danger">
                  <AlertCircle size={12} />
                  Payment failed — update your card below
                </div>
              )}
              {sub?.cancel_at_period_end && (
                <div className="meta-pill meta-pill--warning">
                  <AlertTriangle size={12} />
                  Cancels at end of period
                </div>
              )}
            </div>
            <div className="sub-row__actions">
              {isTrial && (
                <button className="btn btn-primary btn-sm" onClick={() => setShowPlansModal(true)}>
                  <Zap size={13} className="mr-1" /> Subscribe Now
                </button>
              )}
              {isActive && (
                <button className="sety-btn sety-btn-ghost" onClick={() => setShowPlansModal(true)}>
                  <LayoutGrid size={13} className="mr-1" /> Change Plan
                </button>
              )}
              {(!sub || sub.status === "cancelled" || sub.status === "suspended") && (
                <button className="btn btn-primary btn-sm" onClick={() => setShowPlansModal(true)}>
                  <LayoutGrid size={13} className="mr-1" /> Choose Plan
                </button>
              )}
              {isActive && !sub?.cancel_at_period_end && (
                <button className="btn btn-sm btn-outline-danger" onClick={() => setShowCancelConfirm(true)}>
                  Cancel
                </button>
              )}
            </div>
          </div>

          {/* ── Credit Balance Banner ── */}
          {pendingCredit > 0 && (
            <div className="credit-banner">
              <div className="credit-banner__left">
                <div className="credit-banner__icon"><Gift size={15} /></div>
                <div>
                  <div className="credit-banner__label">Account Credit</div>
                  <div className="credit-banner__hint">Applied automatically on your next invoice</div>
                </div>
              </div>
              <div className="credit-banner__amount">{fmt(pendingCredit)}</div>
            </div>
          )}
        </div>
      </div>

      {/* ── Payment Methods ───────────────────────────────────────────── */}
      <div className="billing-section">
        <div className="billing-section__head">
          <h6 className="billing-section__title">Payment Methods</h6>
        </div>
        <div className="billing-section__body--flush">
          {paymentMethods.length === 0 ? (
            <div className="billing-empty">
              <CreditCard size={22} />
              <p>No payment methods saved yet</p>
              <p className="billing-empty__hint">A card is saved automatically after your first payment</p>
            </div>
          ) : (
            <div className="pm-list">
              {paymentMethods.map((pm) => (
                <div key={pm.payment_method_id} className="pm-row">
                  <div className="pm-row__icon"><CreditCard size={15} /></div>
                  <div className="pm-row__info">
                    <div className="pm-row__label">
                      {pm.card_type
                        ? `${pm.card_type.charAt(0).toUpperCase() + pm.card_type.slice(1)} ••••${pm.last4}`
                        : `••••${pm.last4}`}
                      {pm.is_default && <span className="pm-badge">Default</span>}
                    </div>
                    <div className="pm-row__sub">{pm.bank} · Exp {pm.exp_month}/{pm.exp_year}</div>
                  </div>
                  <div className="pm-row__actions">
                    {!pm.is_default && (
                      <button
                        className="icon-btn"
                        title="Set as default"
                        disabled={settingDefault === pm.payment_method_id}
                        onClick={() => handleSetDefault(pm.payment_method_id)}
                      >
                        {settingDefault === pm.payment_method_id
                          ? <RefreshCw size={13} className="spin" />
                          : <Star size={13} />}
                      </button>
                    )}
                    {!pm.is_default && (
                      <button
                        className="icon-btn icon-btn--danger"
                        title="Remove"
                        disabled={removingPm === pm.payment_method_id}
                        onClick={() => handleRemovePm(pm.payment_method_id)}
                      >
                        {removingPm === pm.payment_method_id
                          ? <RefreshCw size={13} className="spin" />
                          : <Trash2 size={13} />}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Invoices ──────────────────────────────────────────────────── */}
      <div className="billing-section">
        <div className="billing-section__head">
          <h6 className="billing-section__title">Invoices</h6>
          {invoices.some(i => i.status === "open") && (
            <span className="unpaid-count">
              {invoices.filter(i => i.status === "open").length} unpaid
            </span>
          )}
        </div>
        <div className="billing-section__body--flush">
          {invoices.length === 0 ? (
            <div className="billing-empty">
              <FileText size={22} />
              <p>No invoices yet</p>
            </div>
          ) : (
            <>
              <div className="invoice-list-header">
                <span>Invoice</span>
                <span>Details</span>
                <span>Amount</span>
                <span></span>
              </div>
              <div className="invoice-list">
                {invoices.map((inv) => {
                  const sc = INVOICE_STATUS[inv.status] || INVOICE_STATUS.draft;
                  const isOpen = inv.status === "open";
                  const billSummary = inv.line_items?.[0]?.description || "Subscription charge";

                  return (
                    <div
                      key={inv.invoice_id}
                      className={`invoice-row ${isOpen ? "invoice-row--open" : ""}`}
                      onClick={() => setSelectedInvoice(inv)}
                    >
                      {/* Col 1: Invoice number + date */}
                      <div className="invoice-row__primary">
                        <div className="invoice-row__num-wrapper">
                          <span className="invoice-row__num">{inv.invoice_number}</span>
                          {isOpen && <span className="invoice-row__urgent-dot" />}
                        </div>
                        <div className="invoice-row__created">{formatDate(inv.created_at)}</div>
                      </div>

                      {/* Col 2: Billing summary + period */}
                      <div className="invoice-row__context">
                        <div className="invoice-row__summary">{billSummary}</div>
                        <div className="invoice-row__period">
                          {formatDate(inv.period_start)} — {formatDate(inv.period_end)}
                        </div>
                      </div>

                      {/* Col 3: Amount + status badge */}
                      <div className="invoice-row__financials">
                        <div className="invoice-row__total">{fmt(inv.total || 0, inv.currency || orgCurrency)}</div>
                        <span className={`inv-badge ${sc.cls}`}>
                          <span className="inv-dot" style={{ background: sc.dot }} />
                          {sc.label}
                        </span>
                      </div>

                      {/* Col 4: Action */}
                      <div className="invoice-row__action" onClick={(e) => e.stopPropagation()}>
                        {isOpen ? (
                          <button
                            className="btn-pay-inline"
                            disabled={payingInvoice === inv.invoice_id}
                            onClick={() => handlePayInvoice(inv.invoice_id)}
                          >
                            {payingInvoice === inv.invoice_id
                              ? <RefreshCw size={13} className="spin" />
                              : <>Pay Now <ArrowUpRight size={13} /></>}
                          {/* amount shown on Pay Now btn handled by invoice row total above */}
                          </button>
                        ) : (
                          <button className="btn-view-detail" onClick={() => setSelectedInvoice(inv)}>
                            View <ChevronRight size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {invoicesMeta?.totalPages > 1 && (
                <div className="billing-load-more">
                  <button
                    className="sety-btn sety-btn-ghost"
                    onClick={() => dispatch(dashActions.fetchAllData("invoices", orgId, { page: 2, limit: 10 }))}
                  >
                    Load more
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Modals ────────────────────────────────────────────────────── */}

      {showPlansModal && (
        <PlansModal
          plans={displayPlans}
          currentPlanId={isLocked ? null : (plan?.plan_id || plan?._id || sub?.plan_id)}
          onSelect={handlePlanSelect}
          subscribingPlan={subscribingPlan}
          onClose={() => setShowPlansModal(false)}
          hasActiveSub={hasActiveSub}
          currency={orgCurrency}
        />
      )}

      {pendingPlanChange && (
        <PlanChangeConfirmModal
          fromPlan={currentPlanObj}
          toPlan={pendingPlanChange.planObj}
          onConfirm={handleConfirmPlanChange}
          onClose={() => setPendingPlanChange(null)}
          loading={confirmingChange}
          currency={orgCurrency}
        />
      )}

      {selectedInvoice && (
        <InvoiceModal
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
          onPay={handlePayInvoice}
          paying={payingInvoice === selectedInvoice.invoice_id}
          orgCurrency={orgCurrency}
        />
      )}

      {paystackData && (
        <PaystackModal
          paymentUrl={paystackData.url}
          invoiceNumber={paystackData.invoiceNumber}
          amount={paystackData.amount}
          currency={orgCurrency}
          onClose={() => setPaystackData(null)}
        />
      )}

      {showPromoModal && (
        <PromoModal
          onApply={handleApplyPromo}
          applying={applyingPromo}
          onClose={() => setShowPromoModal(false)}
        />
      )}

      {showCancelConfirm && (
        <div className="billing-overlay">
          <div className="billing-dialog">
            <h6>Cancel subscription?</h6>
            <p>
              Your access continues until the end of the current billing period.
              You can resubscribe at any time.
            </p>
            <div className="billing-dialog__actions">
              <button className="sety-btn sety-btn-ghost" onClick={() => setShowCancelConfirm(false)}>
                Keep Subscription
              </button>
              <button className="btn btn-danger btn-sm" disabled={cancelling} onClick={handleCancelSubscription}>
                {cancelling && <RefreshCw size={13} className="spin mr-1" />}
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}